import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { fleet, type BulkPreview, type BulkResult } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { FilePick } from "@/components/FilePick";
import { Icon } from "@/components/icons";
import { Select } from "@/components/inputs";
import type { CommandSpec } from "@/modules/fleet/catalogue";

/** Bulk: review, THEN act (OFL-07).
 *
 *  ONE TARGET LIST, NOT TWO. A CSV pick PARSES INTO THE TEXTAREA rather than
 *  arming a parallel hidden list. That is deliberate: a panel with a paste box
 *  and a separate file path has two sources of truth for "who gets stopped",
 *  and the operator cannot see which one the button will use. The console has
 *  already shipped that bug once — one element driving two forms (D-84's
 *  render-test catalogue) — and the blast radius here is vehicles. Filling the
 *  textarea keeps the list visible and editable before anything is sent.
 *
 *  THE NUMBER THAT MATTERS IS `targeted`, NOT `requested`. FOTA taught this
 *  the expensive way: when matched and targeted were allowed to differ
 *  silently, an operator was told a rollout covered a different number of
 *  devices than it would. So the three counts are shown apart and every
 *  skipped asset is named with its reason.
 */
export function BulkPanel({ spec, mayCommand }: {
  spec: CommandSpec;
  mayCommand: boolean;
}) {
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [action, setAction] = useState<number>(spec.options[0]?.action ?? 0);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [done, setDone] = useState<BulkResult | null>(null);
  const [parseError, setParseError] = useState("");

  const assetIds = text.split(/[\s,]+/).map((x) => x.trim())
    .filter((x) => x && !x.startsWith("#"));

  const doPreview = useMutation({
    mutationFn: () => fleet.bulkPreview(assetIds),
    onSuccess: (r) => { setPreview(r); setDone(null); },
  });
  const doRun = useMutation({
    mutationFn: () => fleet.bulkImmobilize(assetIds, action, note || undefined),
    onSuccess: (r) => { setDone(r); setPreview(null); },
  });

  /** The template's own header and comment lines are dropped, so an operator
   *  who downloads it, adds three rows and uploads it back does not get
   *  "asset_id" treated as an asset. */
  const onFile = async (f: File | null) => {
    setFileName(f?.name ?? "");
    setParseError("");
    setPreview(null);
    if (!f) return;
    try {
      const rows = (await f.text()).split(/\r?\n/)
        .map((l) => l.split(",")[0].trim())
        .filter((l) => l && !l.startsWith("#") && l.toLowerCase() !== "asset_id");
      if (rows.length === 0) {
        setParseError("that file has no asset rows in its first column");
        return;
      }
      setText(rows.join("\n"));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  const err = doPreview.isError ? doPreview.error
    : doRun.isError ? doRun.error : null;

  return (
    <div className="panel panel-pad">
      <div style={{ fontSize: 12.5, color: "var(--t-mute)", marginBottom: 10 }}>
        One asset per line, or comma separated. A CSV fills this list so you can
        see and edit it before anything is sent.{" "}
        <a href={fleet.bulkTemplateUrl()}>Download template</a>
      </div>

      <textarea className="inp" rows={4} value={text}
        placeholder={"DRILL-ASSET-001\nDRILL-ASSET-002"}
        onChange={(e) => { setText(e.target.value); setPreview(null); setDone(null); }} />

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                    flexWrap: "wrap", marginTop: 10 }}>
        {spec.options.length > 0 && (
          <div>
            <label className="simlab">{spec.label} as</label>
            <Select style={{ width: 210 }} value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {spec.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </div>
        )}
        <div>
          <label className="simlab">Note (optional)</label>
          <input className="inp" style={{ width: 220 }} placeholder="non-payment, batch 14"
            value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      <ActionRow>
        <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginRight: "auto" }}>
          {assetIds.length} asset{assetIds.length === 1 ? "" : "s"} listed
        </span>
        <FilePick accept=".csv,text/csv" label="Choose .csv" fileName={fileName}
          onFile={onFile} before />
        <button className="btn ghost"
          disabled={assetIds.length === 0 || doPreview.isPending}
          onClick={() => doPreview.mutate()}>Preview</button>
        {/* ARMED ONLY BY A PREVIEW. Not a convenience — the preview is what
            tells the operator how many will ACTUALLY be commanded, and a run
            button live before it would make that review optional. */}
        <button className="btn red"
          disabled={!preview || preview.targeted === 0 || preview.over_limit
                    || !mayCommand || doRun.isPending}
          onClick={() => doRun.mutate()}>
          <Icon name="blocked" /> {spec.label} {preview?.targeted ?? 0}
        </button>
      </ActionRow>

      {!mayCommand && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
          You can preview a run. Sending it needs the immobilise permission.
        </div>
      )}
      {parseError && (
        <div style={{ fontSize: 11.5, color: "var(--bad)" }}>{parseError}</div>
      )}
      {err && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
          {String((err as Error)?.message ?? "bulk run failed")}
        </div>
      )}

      {preview && (
        <div style={{ marginTop: 12, fontSize: 12.5 }}>
          <b>{preview.requested}</b> requested ·{" "}
          <b style={{ color: "var(--bad)" }}>{preview.targeted}</b> will be
          commanded ·{" "}
          <b style={{ color: "var(--warn)" }}>{preview.skipped}</b> skipped
          {preview.over_limit && (
            <div style={{ color: "var(--bad)", marginTop: 6 }}>
              Over the {preview.limit}-asset ceiling. Split the run — the limit
              is about blast radius, not performance.
            </div>
          )}
          {preview.skipped_detail.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {preview.skipped_detail.map((d) => (
                <li key={d.asset_id} className="mono" style={{ fontSize: 11.5 }}>
                  {d.asset_id} — {d.reason === "NOT_ACTUATABLE"
                    ? "no telematics box installed"
                    : "not found, or not yours"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {done && (
        <div style={{ marginTop: 12, fontSize: 12.5 }}>
          Batch <span className="mono">{done.batch_id.slice(0, 8)}</span>:
          commanded <b>{done.commanded}</b> of {done.requested}.
          {done.failed_to_publish.length > 0 && (
            <div style={{ color: "var(--warn)", marginTop: 6 }}>
              {done.failed_to_publish.length} could not be published and are
              still pending. The holds ARE recorded — a bulk run is not atomic,
              and rolling back holds because one publish failed would
              un-immobilise vehicles somebody decided to stop.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
